/* eslint-disable react/destructuring-assignment */
import { withErrorMessage, ChevronDownIcon, ChevronUpIcon } from '@ag1/ui';
import { CheckIcon } from '@portal/shared/studio/ContentPopup/Icons/CheckIcon';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import ReactSelect, {
  ActionMeta,
  components,
  DropdownIndicatorProps,
  InputProps,
  OptionProps
} from 'react-select';
import { InputBaseV2 } from '../InputV2';
import { style } from './Select.styles';
import { OptionType, SelectBaseProps, SelectWithErrorMessageProps } from './Select.types';
import useOptionsCache from './useOptionsCache';

function provideSelectValue(value?: string | string[], options?: OptionType[]) {
  return Array.isArray(value)
    ? options?.filter((o) => value.includes(o.value))
    : (options?.find((o) => o.value === value) ?? null);
}

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        onTouchEnd: (e: React.TouchEvent<HTMLDivElement>) => {
          // e.preventDefault();
          e.stopPropagation();

          if (!selectProps.isDisabled) {
            if (selectProps.menuIsOpen) {
              selectProps.onMenuClose?.();
            } else {
              selectProps.onMenuOpen();
            }
          }
        }
      }}>
      {selectProps.menuIsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </components.DropdownIndicator>
  );
};

const Option = (props: OptionProps) => {
  const { children, isSelected, innerProps } = props;

  return (
    <components.Option
      {...props}
      innerProps={{
        ...innerProps
      }}>
      <div>{children}</div>
      {isSelected && <CheckIcon />}
    </components.Option>
  );
};

// remove autoComplete
const Input = (props: InputProps) => <components.Input {...props} autoComplete="nope" />;

export const SelectBody = (props: SelectBaseProps) => {
  const {
    placeholder,
    value,
    disabled,
    isMulti,
    isLoading,
    options,
    hasError,
    onChange,
    onFocus,
    onBlur,
    id,
    name,
    dense = false,
    mobileOverflow = false,
    ...restProps
  } = props;
  const [optionsCached] = useOptionsCache(options);

  const handleChange = useCallback(
    (option: OptionType | OptionType[] | null, actionMeta: ActionMeta<OptionType>) => {
      const v = Array.isArray(option) ? option.map((o) => o.value) : option?.value;

      if (onChange) {
        onChange(v, option, actionMeta);
      }
    },
    [onChange]
  );

  const { label } = props;

  const selectStyles = useMemo(() => style(hasError, dense, !!label), [hasError, dense, label]);

  return (
    <ReactSelect
      {...restProps}
      inputId={id}
      value={provideSelectValue(value, optionsCached)}
      name={name}
      placeholder={placeholder}
      options={optionsCached}
      styles={selectStyles}
      isDisabled={disabled}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      menuPortalTarget={(() => {
        if (typeof document === 'undefined') {
          return null;
        }
        if (mobileOverflow) {
          return null;
        }
        return document.body;
      })()}
      components={{
        DropdownIndicator,
        Option,
        Input
      }}
      classNamePrefix="ag-select"
      isLoading={isLoading}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

export const SelectBase = forwardRef<HTMLInputElement, SelectBaseProps>((props, ref) => {
  const {
    placeholder,
    label,
    value,
    disabled,
    validDisabled,
    hasError,
    errorMessage,
    helperMessage,
    onBlur,
    onFocus
  } = props;
  const isDisabled = disabled || (validDisabled && !hasError);

  const [focused, setFocused] = useState(false);

  const handleFocus = useCallback(
    (e: any) => {
      setFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: any) => {
      setFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur]
  );

  return (
    <InputBaseV2
      ref={ref}
      label={label}
      placeholder={placeholder}
      value={value}
      disabled={isDisabled}
      hasError={hasError}
      helperMessage={helperMessage}
      errorMessage={errorMessage}
      focusedManually={focused}
      render={() => (
        <SelectBody
          {...props}
          disabled={isDisabled}
          onBlur={handleBlur}
          onFocus={handleFocus}
          isFocused={focused}
        />
      )}
    />
  );
});

if (process.env.NODE_ENV !== 'production') {
  SelectBase.displayName = 'Select';
}

const SelectWithErrorMessage = withErrorMessage<HTMLInputElement, SelectWithErrorMessageProps>(
  SelectBase
);

const Select = forwardRef<HTMLInputElement, SelectWithErrorMessageProps>((props, ref) => (
  <SelectWithErrorMessage ref={ref} {...props} />
));

if (process.env.NODE_ENV === 'development') {
  Select.displayName = 'Select';
}

export default Select;
